import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ArticleView } from '../_generated/model/models';
import { getTimeAgoFromString } from '../shared/utils';
import { UseUIBasicIconDirective } from '../ui-components/atoms/icons/ui-basic.directive';
import { articlesFeature, ArticlesState } from './';
import { LinkParserService } from './link-parser.service';

@Component({
  selector: 'dmcom-article-detail',
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, UseUIBasicIconDirective],
  template: `
    <div class="article-detail">
      <!-- Loading state -->
      @if (loading()) {
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading article...</p>
        </div>
      }

      <!-- Error state -->
      @if (error(); as errorMessage) {
        <div class="error-container">
          <h2>Error</h2>
          <p>{{ errorMessage }}</p>
          <button (click)="goBack.emit()" class="back-button">Go Back</button>
        </div>
      }

      <!-- Article content -->
      @if (!loading() && !error() && article(); as articleData) {
        <div class="article-container">
          <div class="article-content-wrapper">
            <!-- Article Header -->
            <div>
              <div>
                <div class="article-category">
                  <svg
                    [dmcomUseUIBasicIcon]="
                      articleData.category === 'form_update' ? 'form' : 'legal'
                    "
                    class="category-icon"
                  ></svg>
                  <span class="category-text">{{
                    getCategoryDisplayName(articleData.category)
                  }}</span>
                  <span class="separator">•</span>
                  <span class="date">{{
                    getTimeAgo(articleData.createdAt)
                  }}</span>
                </div>
              </div>

              <h1 class="article-title">{{ articleData.title }}</h1>
            </div>

            <!-- Article Content -->
            <div
              class="content-wrapper"
              [innerHTML]="formattedContent"
              (click)="onContentClick($event)"
            ></div>

            <!-- Article Footer -->
            <div class="article-footer">
              <div class="article-info">
                <p class="last-updated">
                  Last updated: {{ formatDate(articleData.updatedAt) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Article not found -->
      @if (!loading() && !error() && !article()) {
        <div class="not-found-container">
          <h2>Article Not Found</h2>
          <p>The requested article could not be found.</p>
          @if (showGoBackButton()) {
            <button (click)="goBack.emit()" class="back-button">Go Back</button>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .article-detail {
        height: 100%;
        width: 100%;
        background: transparent;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      .loading-container,
      .error-container,
      .not-found-container {
        max-width: 800px;
        margin: 0 auto;
        text-align: center;
        padding: 4rem 2rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }

      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f4f6;
        border-top: 4px solid var(--Primary-500, #4e22ba);
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .back-button {
        background: var(--color-primary-500);
        color: white;
        border: none;
        padding: 0.75rem 1.5rem;
        border-radius: 0.5rem;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
        margin-top: 1rem;
      }

      .back-button:hover {
        background: var(--color-primary-700);
      }

      /* Article Container Styles */
      .article-container {
        height: 100%;
        background: white;
        width: 100%;
      }

      .article-content-wrapper {
        max-width: 680px;
        margin: 0 auto;
      }

      .article-category {
        display: flex;
        align-items: center;
        font-size: 0.875rem;
        color: var(--color-primary-500);
      }

      .category-icon {
        width: 22px;
        height: 22px;
        margin-bottom: 2px;
        margin-right: 2px;
        flex-shrink: 0;
      }

      .category-text {
        font-weight: 500;
      }

      .separator {
        color: #6b7280;
        margin: 0 6px;
      }

      .date {
        color: #6b7280;
      }

      .article-title {
        font-size: 32px;
        font-weight: 700;
        line-height: 140%;
        color: var(--color-neutral-50);
        margin-top: 12px;
      }

      .content-wrapper {
        font-size: 1rem;
        line-height: 1.7;
        color: var(--color-neutral-50);
      }

      .content-wrapper h1,
      .content-wrapper h2,
      .content-wrapper h3,
      .content-wrapper h4,
      .content-wrapper h5,
      .content-wrapper h6 {
        color: var(--color-neutral-50);
        font-weight: 600;
        margin-top: 20px;
      }

      .content-wrapper h1 {
        font-size: 1.75rem;
      }

      .content-wrapper h2 {
        font-size: 1.5rem;
      }

      .content-wrapper h3 {
        font-size: 1.25rem;
      }

      .content-wrapper p {
        margin-top: 6px;
        margin-bottom: 24px;
        font-size: 16px;
        font-weight: 400;
      }

      .content-wrapper p:has(u) + p,
      .content-wrapper p:has(b) + p {
        margin-top: 8px;
      }

      .content-wrapper u {
        text-decoration: none;
        font-weight: 600;
        font-size: 18px;
      }

      .content-wrapper ul,
      .content-wrapper ol {
        margin-bottom: 1rem;
        padding-left: 1.5rem;
      }

      .content-wrapper li {
        margin-bottom: 0.5rem;
      }

      .content-wrapper blockquote {
        border-left: 4px solid var(--color-primary-500);
        padding-left: 1rem;
        margin: 1.5rem 0;
        font-style: italic;
        color: #6b7280;
      }

      .content-wrapper code {
        background-color: #f3f4f6;
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-family: 'Courier New', monospace;
        font-size: 0.875rem;
      }

      .content-wrapper pre {
        background-color: #f3f4f6;
        padding: 1rem;
        border-radius: 0.5rem;
        overflow-x: auto;
        margin: 1rem 0;
      }

      .content-wrapper pre code {
        background-color: transparent;
        padding: 0;
      }

      .content-wrapper a {
        color: var(--color-primary-500);
        font-weight: 700;
        text-decoration: none !important;
        transition: color 0.2s ease;
      }

      .content-wrapper a:hover {
        color: var(--color-primary-700);
      }

      /* Styles pour les liens internes */
      .content-wrapper a.internal-link {
        color: var(--color-primary-600);
        position: relative;
      }

      .content-wrapper a.internal-link:hover {
        color: var(--color-primary-800);
      }

      .content-wrapper a.internal-link::after {
        content: '↗';
        font-size: 0.8em;
        margin-left: 2px;
        opacity: 0.7;
      }

      /* Styles pour les liens externes */
      .content-wrapper a.external-link {
        color: var(--color-primary-500);
      }

      .content-wrapper a.external-link:hover {
        color: var(--color-primary-700);
      }

      .content-wrapper a.external-link::after {
        content: '↗';
        font-size: 0.8em;
        margin-left: 2px;
        opacity: 0.7;
      }

      .content-wrapper table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0;
      }

      .content-wrapper th,
      .content-wrapper td {
        border: 1px solid #e5e7eb;
        padding: 0.75rem;
        text-align: left;
      }

      .content-wrapper th {
        background-color: #f9fafb;
        font-weight: 600;
      }

      .article-footer {
        margin-top: 2rem;
        padding-top: 1rem;
        border-top: 1px solid #e5e7eb;
      }

      .article-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .last-updated {
        font-size: 0.875rem;
        color: #6b7280;
        margin: 0;
      }

      .float-right {
        float: unset;
      }

      div.quote {
        margin-top: 27px;
      }
      .quote,
      .quote p {
        font:
          italic bold 18px arial,
          helvetica,
          'times new roman';
        margin: 0;
        width: 272px;
      }
      .quote a {
        font-style: normal;
      }
      .quote p.client {
        font-size: 13px;
        font-style: italic;
        font-weight: normal;
        margin-top: 4px;
      }
    `,
  ],
})
export class ArticleDetailComponent {
  public readonly articleData = input<ArticleView | null>(null);
  public readonly showGoBackButton = input<boolean>(true);

  public readonly article = signal<ArticleView | null>(null);
  public readonly goBack = output<void>();

  private readonly articlesStore = inject<Store<ArticlesState>>(Store);
  private readonly linkParserService = inject(LinkParserService);

  public readonly loading = this.articlesStore.selectSignal(
    articlesFeature.selectIsLoading,
  );
  public readonly error = this.articlesStore.selectSignal(
    articlesFeature.selectError,
  );

  public formattedContent = '';
  public getTimeAgo = getTimeAgoFromString;

  constructor() {
    // Watch for changes in articleData input to update article signal
    effect(() => {
      const articleData = this.articleData();
      this.article.set(articleData);
    });

    // Watch for changes in article to format content
    effect(() => {
      const articleData = this.article();
      if (articleData) {
        this.formatContent(articleData.content);
      }
    });
  }

  getCategoryDisplayName(category: string): string {
    return category === 'legal_regulatory_update'
      ? 'Legal & Regulatory Update'
      : 'Form Update';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  onContentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (link?.href) {
      const originalHref = link.getAttribute('data-original-href') ?? link.href;
      const processedLink = this.linkParserService.processLink(originalHref);

      this.linkParserService.handleLinkClick(
        event,
        processedLink.href,
        processedLink.isInternal,
        processedLink.nodeId,
      );
    }
  }

  private formatContent(content: string): void {
    if (!content || content.trim() === '') {
      this.formattedContent = '<p>No content available.</p>';
      return;
    }

    // Basic content formatting
    let formattedContent = content;

    // Convert line breaks to paragraphs
    formattedContent = formattedContent.replace(/\n\n/g, '</p><p>');
    formattedContent = formattedContent.replace(/\n/g, ' ');

    // Wrap in paragraph tags if not already wrapped
    if (!formattedContent.startsWith('<p>')) {
      formattedContent = '<p>' + formattedContent + '</p>';
    }

    formattedContent = formattedContent.replace(/<\?php[\s\S]*?\?>/g, '');

    formattedContent = formattedContent
      // Links - use the link parsing service
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, href) => {
        const processedLink = this.linkParserService.processLink(
          href as string,
        );
        let linkHtml = `<a href="${processedLink.href}" data-original-href="${href}"`;

        if (processedLink.target) {
          linkHtml += ` target="${processedLink.target}"`;
        }

        if (processedLink.rel) {
          linkHtml += ` rel="${processedLink.rel}"`;
        }

        if (processedLink.isInternal) {
          linkHtml += ` class="internal-link"`;
        } else {
          linkHtml += ` class="external-link"`;
        }

        linkHtml += `>${text}</a>`;
        return linkHtml;
      })
      // Lists
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>');

    formattedContent = formattedContent.replace(
      /bgcolor="#000000"/gi,
      'bgcolor="#FFFFFF"',
    );

    // Process existing links in HTML with the parsing service
    formattedContent =
      this.linkParserService.processContentLinks(formattedContent);

    // Fix all relative image paths by prefixing with DocMagic domain
    // Handle relative paths starting with /sites/default/files/

    // Handle any other relative image paths (catch-all for any /path/ that starts with /)
    formattedContent = formattedContent.replace(
      /src="\/(?!\/)([^"]*\.(jpg|jpeg|png|gif|svg|webp|bmp|ico))"/g,
      'src="https://www.docmagic.com/$1"',
    );

    // Remove all br tags
    formattedContent = formattedContent.replace(/<br\s*\/?>/gi, ' ');

    // Remove empty paragraphs
    formattedContent = formattedContent.replace(/<p>\s*<\/p>/g, '');
    formattedContent = formattedContent.replace(/<p>\s*&nbsp;\s*<\/p>/g, '');

    // Wrap consecutive list items in ul tags
    formattedContent = formattedContent.replace(
      /(<li>.*<\/li>)/gs,
      '<ul>$1</ul>',
    );
    this.formattedContent = formattedContent;
  }
}
